import styled from "styled-components";
import { FC, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";

import { Td } from "@/components/Table";
import Text from "@/components/Text";
import GamificationWinnerModal from "./GamificationWinnerModal";
import { TestingCompetitionBonusResource } from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { RootState } from "@/utils/store";
import ProjectsSequenceBadge from "@/pages/GamificationProjects/components/ProjectsSequenceBadge";

const ButtonsHolder = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  width: 24px;
  height: 24px;
  background: #ecf9f1;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.success};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: solid 1px transparent;
  transition: border-color ease-out 0.2s;
  &:hover {
    border-color: ${({ theme }) => theme.colors.success};
  }
`;

const SecondaryActionButton = styled(ActionButton)`
  background: ${({ theme }) => theme.colors.errorLight};
  color: ${({ theme }) => theme.colors.error};
  &:hover {
    border-color: ${({ theme }) => theme.colors.error};
  }
`;

const Bonus = styled.div`
  display: flex;
  align-items: center;
`;

const Dot = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.success};
  margin-right: 8px;
`;

const StyledText = styled(Text)`
  margin-top: 4px;
  display: inline-block;
`;

export const TeamIcon = styled.img`
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  width: 16px;
  height: 16px;
  object-fit: cover;
  border-radius: 50%;
  margin-right: 6px;
`;

export const TeamInfo = styled.div`
  display: flex;
  align-items: center;
`;

interface Props {
  bonus: TestingCompetitionBonusResource;
}

const PendingBonusesTableRow: FC<Props> = ({ bonus }) => {
  const [showWinnerModal, setShowWinnerModal] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onApprove = async () => {
    try {
      const confirm = await alerts.confirm(
        `Are you sure you want to approve ${bonus.userName} as a winner?`
      );

      if (!confirm) return;

      await api.patch(`/v1/gamification/thumbnails/bonuses/${bonus.id}`, {
        status: "approved",
      });

      await queryClient.invalidateQueries(["bonuses", { type: "thumbnails" }], {
        exact: false,
      });

      alerts.success("Winner was succesfully approved");
    } catch (_) {
      alerts.error();
    }
  };

  const teamIcon = useSelector(
    (state: RootState) => state.teams[bonus.teamId].icon?.versions?.icon
  );

  return (
    <>
      <tr>
        <Td>
          {bonus.projectName}
          <br />
          <StyledText $type="extraSmall" $color="gray" as="span">
            {bonus.projectId}
          </StyledText>
        </Td>
        <Td>
          <TeamInfo>
            {!!teamIcon && (
              <TeamIcon src={teamIcon} alt={`${bonus.teamName} icon`} />
            )}
            {bonus.teamName}
          </TeamInfo>
        </Td>
        <Td>
          <ProjectsSequenceBadge testSequence={bonus.cBatch} />
        </Td>
        <Td>
          {DateTime.fromISO(bonus.endedAt).toRelative()}
          <br />
          <StyledText $type="extraSmall" $color="gray" as="span">
            {DateTime.fromISO(bonus.endedAt).toFormat("yyyy/LL/dd")}
          </StyledText>
        </Td>
        <Td>{bonus.userName}</Td>
        <Td>{bonus.ctr} %</Td>
        <Td>
          <Bonus>
            <Dot />€ {bonus.reward}
          </Bonus>
        </Td>
        <Td>
          <ButtonsHolder>
            <ActionButton onClick={onApprove}>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.125 3.37524L4.875 8.62501L2.25 6.00024"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ActionButton>
            <SecondaryActionButton onClick={() => setShowWinnerModal(true)}>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 3L3 9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 9L3 3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </SecondaryActionButton>
          </ButtonsHolder>
        </Td>
      </tr>
      <AnimatePresence>
        {showWinnerModal && (
          <GamificationWinnerModal
            onClose={() => setShowWinnerModal(false)}
            bonusId={bonus.id}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default PendingBonusesTableRow;
